import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import { Box, Button, TextField } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import Title from "../../common/Title";

import useAxios from "../../../hooks/useAxios";
import useAlert from "../../../hooks/useAlert";
import { __batchResetPort } from "../../../api/mdn";

const MDNResetForm = () => {
  const nav = useNavigate();

  const { setAlertMessage } = useAlert();
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [formData, setFormData] = useState(null);
  const [response, call, loading] = useAxios({
    ...__batchResetPort,
  });
  const initialData = { lines: [] };

  useEffect(() => {
    if (formData) {
      call({
        data: linesData,
      });
    }
  }, [formData]);

  useEffect(() => {
    formik.setSubmitting(false);
    if (!response) return;
    if (response.status) {
      response.data?.success &&
        response.data?.success.length &&
        !response.data?.error?.length &&
        setAlertMessage({
          type: "success",
          message: `Success: ${response.data.success.join(", ")}`,
        });
      response.data?.error &&
        response.data?.error.length &&
        !response.data?.success?.length &&
        setAlertMessage({
          type: "error",
          message: `Error: ${response.data.error.join(", ")}`,
        });

      response.data?.error.length &&
        response.data?.success?.length &&
        setAlertMessage({
          type: "info",
          message: (
            <>
              {`Success: ${response.data.success.join(", ")}`} <br />
              {`Error: ${response.data.error.join(", ")}`}
            </>
          ),
        });

      !response.data?.error?.length && nav(`/mdn`);
    }
    validateFunction(rawData);
  }, [response]);

  useEffect(() => {
    formik.setErrors(validateFunction(rawData));
  }, [rawData]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];
    if (!data) {
      return formik.setErrors(errors);
    }

    const lines = data.split("\n");
    lines.some((line, idx) => {
      const data = line.split(",");
      if (data.length !== 1) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }
      try {
        const line_data = line.split(",");
        const obtainedData = { line: line_data[0].trim() };

        if (isNaN(obtainedData.line.trim())) {
          errors.lines = `Invalid number in line ${idx + 1}: ${line}`;
          return true;
        }
        if (
          obtainedData.line.trim().length !== 10 &&
          (obtainedData.line.trim().length > 20 ||
            obtainedData.line.trim().length < 19)
        ) {
          throw new Error(`Invalid number in line ${idx + 1}: ${line}`);
        }
        formatedLines.push(obtainedData);
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    } else {
      setLinesData([]);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        obj[key] = values[key] || null;
        return obj;
      }, {});

      setFormData(requestData);
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors?.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%", mb: 5 }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content={"Reset Ports"}
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
          maxWidth={350}
        >
          <Grid item xs={12} minWidth={"100%"}>
            <FormControl
              error={Boolean(formik.errors?.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors?.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors?.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={"100%"}>
            <InputLabel htmlFor="lines">
              {"Add in the format. "}
              <br></br>
              {"ICCID/MDN"}
              <br></br>
              {"ICCID/MDN"}
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors?.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextField
                multiline
                rows={6}
                aria-label="iccids"
                placeholder={`Add in the format\nICCID/MDN\nICCID/MDN`}
                name="lines"
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                }}
              />
              {Boolean(formik.errors?.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors?.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                disabled={
                  !linesData || !linesData.length || formik.errors?.lines
                }
                onClick={() =>
                  !loading && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={loading}
                variant="contained"
              >
                {"Reset Ports"}
              </LoadingButton>
            </Grid>
            <Grid item>
              <LoadingButton
                size="large"
                onClick={() => nav("/mdn")}
                loading={loading}
                variant="contained"
              >
                Cancel
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default MDNResetForm;
